"use strict";
/**
 * Thread server → local cache pull.
 *
 * Model (stable, reusable for other list domains):
 * - Server is source of truth
 * - localStorage is a write-through cache (pills / digest-stable ng-repeat)
 * - Pull replaces cache; mutations go API-first then upsert cache
 * - No offline queue, no local→server migrate re-push
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createThreadPullGate = exports.pullThreadsToCache = void 0;
var threadStore_1 = require("./threadStore");
/**
 * Flatten members from list-with-includeMembers payload, or fall back to per-thread GET.
 * Always normalize so camelCase + snake_case table fields land in the cache.
 */
function collectMembersFromList(list) {
    // If any thread has a members array (even empty), treat as bulk response.
    var anyEmbedded = list.some(function (t) { return Array.isArray(t.members); });
    if (!anyEmbedded)
        return null;
    var members = [];
    for (var _i = 0, list_1 = list; _i < list_1.length; _i++) {
        var t = list_1[_i];
        var ml = t.members;
        if (!Array.isArray(ml))
            continue;
        for (var _a = 0, ml_1 = ml; _a < ml_1.length; _a++) {
            var raw = ml_1[_a];
            var m = (0, threadStore_1.normalizeMemberRecord)((raw === null || raw === void 0 ? void 0 : raw.threadId) != null || (raw === null || raw === void 0 ? void 0 : raw.thread_id) != null
                ? raw
                : __assign(__assign({}, raw), { threadId: t.id }));
            if (m)
                members.push(m);
        }
    }
    return members;
}
/**
 * GET all threads for person (prefer includeMembers), replace local cache.
 * Pure of UI — caller invalidates menus / refreshes pills.
 */
function pullThreadsToCache(personId, deps, opts) {
    var _a;
    var limit = (_a = opts === null || opts === void 0 ? void 0 : opts.limit) !== null && _a !== void 0 ? _a : 200;
    var threadsApi = deps.threadsApi, storage = deps.storage;
    return Promise.resolve(threadsApi.listThreads({ limit: limit, includeMembers: true })).then(function (threads) {
        var list = (threads || [])
            .map(function (t) { return (0, threadStore_1.normalizeThreadRecord)(t); })
            .filter(function (t) { return !!t; });
        var embedded = collectMembersFromList(threads || []);
        if (embedded) {
            (0, threadStore_1.replacePersonThreadCache)(personId, list, embedded, storage);
            return { threads: list, members: embedded };
        }
        // Fallback: older API without includeMembers — N+1 listMembers
        var memberJobs = list.map(function (t) {
            return Promise.resolve(threadsApi.listMembers(t.id)).then(function (m) {
                return (m || [])
                    .map(threadStore_1.normalizeMemberRecord)
                    .filter(function (x) { return !!x; });
            }, function () { return []; });
        });
        return Promise.all(memberJobs).then(function (memberLists) {
            var members = [];
            for (var _i = 0, memberLists_1 = memberLists; _i < memberLists_1.length; _i++) {
                var ml = memberLists_1[_i];
                for (var _a = 0, _b = ml || []; _a < _b.length; _a++) {
                    var m = _b[_a];
                    members.push(m);
                }
            }
            (0, threadStore_1.replacePersonThreadCache)(personId, list, members, storage);
            return { threads: list, members: members };
        });
    });
}
exports.pullThreadsToCache = pullThreadsToCache;
/** Coalesce concurrent pulls for the same person. */
function createThreadPullGate() {
    var inFlight = null;
    var inFlightPerson = null;
    return function pullOnce(personId, deps, opts) {
        var key = String(personId);
        if (inFlight && inFlightPerson === key) {
            return inFlight;
        }
        inFlightPerson = key;
        inFlight = pullThreadsToCache(personId, deps, opts).then(function (r) {
            inFlight = null;
            inFlightPerson = null;
            return r;
        }, function (err) {
            inFlight = null;
            inFlightPerson = null;
            return Promise.reject(err);
        });
        return inFlight;
    };
}
exports.createThreadPullGate = createThreadPullGate;
